import { useEffect, useRef, useState } from 'react'

/**
 * Precarga de imágenes con reintentos, compartida por las marcas del producto
 * (`VerSportsLogo`, los dos `ClubMark`, el `ClubIcon` del `NavBar`).
 *
 * Por qué existe (bug mg 2026-09-07, "sigo viendo imagen rota en transiciones,
 * ej. al actualizar en /vr/"): montar un `<img>` visible y esperar el `onError`
 * llega tarde — entre que la request muere y React re-renderiza, Chrome ya
 * pintó el glifo de imagen rota. Y las requests mueren seguido sin que el
 * asset esté mal: un cambio de ruta, el reload que limpia `?token`, el remonte
 * de `RequireAuth` o un restart de vite abortan lo que estaba en vuelo.
 *
 * La regla es entonces: la imagen se carga fuera de pantalla (`new Image()`) y
 * recién se monta el `<img>` visible cuando terminó de cargar. Mientras tanto
 * el caller pinta su propio fallback (caja del mismo tamaño, iniciales, ícono
 * genérico), así que nunca hay glifo ni texto de `alt` a la vista.
 *
 * Los reintentos van con backoff porque el modo de falla típico es un servidor
 * que no está por un segundo (deploy, vite reiniciando): reintentar tres veces
 * seguidas en el mismo frame quema los tres intentos contra la misma caída.
 */

export type ImageStatus = 'idle' | 'loading' | 'loaded' | 'failed'

/**
 * Espera antes de cada reintento. `[400, 1200, 3000]` = 1 intento inicial + 3
 * reintentos, el último ~4.6 s después del primer fallo.
 */
export const DEFAULT_IMAGE_RETRY_DELAYS_MS: readonly number[] = [400, 1200, 3000]

export interface PreloadImageOptions {
  /** Esperas (ms) antes de cada reintento. Default `DEFAULT_IMAGE_RETRY_DELAYS_MS`. */
  delaysMs?: readonly number[]
  /** Se llama una sola vez, con el resultado final. No se llama si se canceló. */
  onResult: (loaded: boolean) => void
  /** Inyectable para tests. Default `() => new Image()`. */
  createImage?: () => HTMLImageElement
}

/**
 * Carga `src` fuera del árbol, reintentando con backoff. Devuelve la función
 * de cancelación: corta timers pendientes y desengancha los handlers, así que
 * después de llamarla `onResult` no se ejecuta nunca.
 */
export function preloadImage(src: string, options: PreloadImageOptions): () => void {
  const delays = options.delaysMs ?? DEFAULT_IMAGE_RETRY_DELAYS_MS
  const createImage = options.createImage ?? (() => new Image())

  let cancelled = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let current: HTMLImageElement | null = null

  function detach(img: HTMLImageElement) {
    img.onload = null
    img.onerror = null
  }

  function attempt(index: number) {
    if (cancelled) return
    const img = createImage()
    current = img
    img.decoding = 'async'
    img.onload = () => {
      if (cancelled) return
      detach(img)
      current = null
      options.onResult(true)
    }
    img.onerror = () => {
      if (cancelled) return
      detach(img)
      current = null
      // `delays[index]` es la espera hasta el intento `index + 1`; cuando se
      // acaba el array no quedan reintentos y el fallo pasa a definitivo.
      const delay = delays[index]
      if (delay === undefined) {
        options.onResult(false)
        return
      }
      timer = setTimeout(() => {
        timer = null
        attempt(index + 1)
      }, delay)
    }
    // El `src` va último: si el asset ya está en cache, los handlers pueden
    // dispararse de forma síncrona al asignarlo.
    img.src = src
  }

  attempt(0)

  return () => {
    cancelled = true
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (current) {
      detach(current)
      current = null
    }
  }
}

export interface UseImageStatusOptions {
  /** Ver `preloadImage`. Se compara por valor, no por identidad del array. */
  delaysMs?: readonly number[]
}

/**
 * Estado de precarga de `src`. `'idle'` sin `src`, `'loading'` mientras se
 * intenta (incluidos los reintentos), `'loaded'` / `'failed'` al terminar.
 * Cambiar `src` reinicia el ciclo y desmontar cancela timers y handlers.
 */
export function useImageStatus(
  src: string | null | undefined,
  options?: UseImageStatusOptions,
): ImageStatus {
  const delays = options?.delaysMs
  // La dependencia del efecto es el contenido del array, no su identidad: un
  // caller que pasa un literal en línea no debe reiniciar la carga en cada
  // render.
  const delaysKey = delays ? delays.join(',') : ''
  const delaysRef = useRef(delays)
  delaysRef.current = delays

  const [status, setStatus] = useState<ImageStatus>(src ? 'loading' : 'idle')

  useEffect(() => {
    if (!src) {
      setStatus('idle')
      return
    }
    setStatus('loading')
    return preloadImage(src, {
      delaysMs: delaysRef.current,
      onResult: (loaded) => setStatus(loaded ? 'loaded' : 'failed'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- delaysKey resume delaysRef
  }, [src, delaysKey])

  return status
}
